import React, { memo, useCallback, useContext, useMemo, useState } from 'react';
import {
  Tooltip,
  Button,
  message,
  Modal,
  Row,
  DatePicker,
  Form,
  Typography,
  Col,
} from 'antd';
import { CopyOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import SessionContext from '@providers/Session/SessionContext';
import api from '@services/api';
import rolesConstants, { RolesTypes } from '@utils/rolesConstants';
import moment from 'moment';
import SearchUploader from '@components/SearchUploader';

const RequestDailySessionReport = () => {
  const { t } = useTranslation('reports');
  const navigate = useNavigate();
  const { effectiveRoles } = useContext(SessionContext);

  const [isRequesting, setIsRequesting] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedMsisdns, setSelectedMsisdns] = useState<string>();
  const [selectedImsis, setSelectedImsis] = useState<string>();
  const [form] = Form.useForm();

  const hasRole = useCallback(
    (role: RolesTypes) => effectiveRoles?.portal?.includes(role),
    [effectiveRoles],
  );

  const navigateToReports = useCallback(
    (type: string) => {
      message.destroy('report-requested');
      navigate(`/support/reports?type=${type}`);
    },
    [navigate],
  );

  const handleClose = useCallback(() => {
    setIsModalVisible(false);
    setSelectedMsisdns(undefined);
    setSelectedImsis(undefined);
  }, []);

  const handleRequest = useCallback(
    (values: any) => {
      setIsRequesting(true);
      api({
        method: 'post',
        url: `/service-proxy/accts/by-day/report`,
        data: {
          initialDate: values.startDate.format('YYYY-MM-DD'),
          endDate: values.endDate.format('YYYY-MM-DD'),
          msisdn: selectedMsisdns ? selectedMsisdns.split(',') : [],
          imsi: selectedImsis ? selectedImsis.split(',') : [],
        },
      })
        .then(() => {
          message.success({
            content: (
              <span>
                {t('request.report-success')}
                <Button
                  onClick={() => navigateToReports('RADACCT_SESSIONS_DAYS')}
                  type="link"
                  style={{ padding: 3 }}
                >
                  {t('request.go-to-reports')}
                </Button>
                {t('request.check-report-progress-and-download')}
              </span>
            ),
            key: 'report-requested',
            duration: 5,
          });
        })
        .catch(() => message.error(t('request.report-error')))
        .finally(() => {
          setIsRequesting(false);
          handleClose();
        });
    },
    [selectedMsisdns, selectedImsis, t, navigateToReports, handleClose],
  );

  const showExportModal = useCallback(() => {
    setIsModalVisible(true);
  }, []);

  const handleDateChange = () => {
    const startDate = form.getFieldValue('startDate');
    const endDate = form.getFieldValue('endDate');
    if (startDate && endDate) {
      const maxEndDate = startDate.clone().add(3, 'months');
      if (endDate.isAfter(maxEndDate)) {
        form.setFieldsValue({
          endDate: maxEndDate,
        });
      }
    }
  };

  const button = useMemo(
    () => (
      <Button
        icon={<CopyOutlined />}
        disabled={isRequesting}
        loading={isRequesting}
        onClick={e => {
          e.stopPropagation();
          showExportModal();
        }}
        className="report-request-button"
      />
    ),
    [isRequesting, showExportModal],
  );

  return (
    <>
      {hasRole(rolesConstants.REQUEST_REPORT) && (
        <Tooltip title={t('request.report')}>{button}</Tooltip>
      )}

      <Modal
        title={<h3>{t('request.report')}</h3>}
        open={isModalVisible}
        onCancel={handleClose}
        footer={[
          <Button key="cancel" onClick={handleClose}>
            {t('request.buttons.cancel')}
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={isRequesting}
            onClick={() => form.submit()}
          >
            {t('request.buttons.confirm')}
          </Button>,
        ]}
        width={400}
      >
        <Form
          form={form}
          onFinish={handleRequest}
          layout="vertical"
          initialValues={{
            startDate: moment().subtract(1, 'days'),
            endDate: moment(),
          }}
          style={{ padding: 16 }}
        >
          <Row gutter={[16, 0]}>
            <Col lg={24} xl={12} xs={24}>
              <Form.Item
                label={t('request.startDate')}
                name="startDate"
                rules={[
                  {
                    required: true,
                    message: t('request.fieldRequired'),
                  },
                ]}
              >
                <DatePicker
                  format="DD/MM/YYYY"
                  style={{ width: '100%' }}
                  onChange={handleDateChange}
                  allowClear={false}
                  disabledDate={current => {
                    const beforeDate =
                      current < moment().subtract(90, 'days').startOf('day');
                    const afterDates = current > moment().endOf('day');
                    return current && (beforeDate || afterDates);
                  }}
                />
              </Form.Item>
            </Col>
            <Col lg={24} xl={12} xs={24}>
              <Form.Item
                label={t('request.endDate')}
                name="endDate"
                dependencies={['startDate']}
                rules={[
                  { required: true, message: t('request.fieldRequired') },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const startDate = getFieldValue('startDate');
                      if (startDate && value) {
                        const maxEndDate = startDate.clone().add(3, 'months');
                        if (startDate.isAfter(value)) {
                          return Promise.reject(
                            new Error(t('request.fieldRequired')),
                          );
                        }
                        if (value.isAfter(maxEndDate)) {
                          form.setFieldsValue({
                            endDate: maxEndDate,
                          });
                        }
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <DatePicker
                  format="DD/MM/YYYY"
                  style={{ width: '100%' }}
                  onChange={handleDateChange}
                  allowClear={false}
                  disabledDate={current => {
                    const beforeDate =
                      current < moment().subtract(90, 'days').startOf('day');
                    const afterDates = current > moment().endOf('day');
                    return current && (beforeDate || afterDates);
                  }}
                />
              </Form.Item>
            </Col>
            <Col lg={24} xl={24} xs={24} style={{ paddingBottom: '24px' }}>
              <Typography style={{ padding: '8px' }}>
                MSISDN
                <Tooltip title={t('request.tooltip', { field: 'MSISDN' })}>
                  <InfoCircleOutlined style={{ marginLeft: 8 }} />
                </Tooltip>
              </Typography>
              <SearchUploader
                columnName="MSISDN"
                onChange={(e: any) => {
                  setSelectedMsisdns(e.target.value);
                }}
                onUpload={(value: string[]) => {
                  setSelectedMsisdns(value.join(','));
                }}
                style={{
                  width: '100%',
                }}
                value={selectedMsisdns}
                allowClear
              />
            </Col>

            <Col lg={24} xl={24} xs={24}>
              <Typography style={{ padding: '8px' }}>
                IMSI
                <Tooltip title={t('request.tooltip', { field: 'IMSI' })}>
                  <InfoCircleOutlined style={{ marginLeft: 8 }} />
                </Tooltip>
              </Typography>
              <SearchUploader
                columnName="IMSI"
                onChange={(e: any) => {
                  setSelectedImsis(e.target.value);
                }}
                onUpload={(value: string[]) => {
                  setSelectedImsis(value.join(','));
                }}
                style={{
                  width: '100%',
                }}
                value={selectedImsis}
                allowClear
              />
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default memo(RequestDailySessionReport);
